export const APP_THEME = 'app-theme';
/** Same key as Rows — theme preference on this browser/device. */
export const THEME_STORAGE_KEY = 'app-theme-preference';

/** Resolved theme applied to `html[data-theme]`. */
export type AppTheme = 'light' | 'dark';

/**
 * User preference stored in localStorage / account settings.
 * `'system'` is legacy (OS follow); normalize maps it to light.
 */
export type ThemePreference = AppTheme | 'system';

export const DEFAULT_APP_THEME: AppTheme = 'light';
/** Used when nothing is saved in localStorage — always light, never OS. */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

/**
 * When either class is on `html`, dark mode is ignored and light is forced.
 * Keep in sync with `apply-a11y-preferences.ts` class names.
 */
const A11Y_FORCE_LIGHT_CLASSES = [
  'a11y-login-mode',
  'a11y-high-contrast',
] as const;

/** Last theme requested by preference (before a11y light override). */
let lastRequestedTheme: AppTheme = DEFAULT_APP_THEME;

/** Light/dark only; legacy `'system'` and unknown values become light. */
export const normalizeThemePreference = (value: unknown): AppTheme => {
  if (value === 'dark') return 'dark';
  return 'light';
};

export const getSystemTheme = (): AppTheme => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return DEFAULT_APP_THEME;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

export const resolveAppTheme = (preference: ThemePreference): AppTheme =>
  preference === 'dark' ? 'dark' : 'light';

/** Accessibility mode (login toggle or high contrast) fully disables dark theme. */
export const isA11yForcingLightTheme = (): boolean => {
  if (typeof document === 'undefined') return false;
  const html = document.documentElement;
  return A11Y_FORCE_LIGHT_CLASSES.some((className) =>
    html.classList.contains(className)
  );
};

export const applyAppTheme = (theme: AppTheme): void => {
  if (typeof document === 'undefined') return;
  lastRequestedTheme = theme;
  document.documentElement.dataset.theme = isA11yForcingLightTheme()
    ? 'light'
    : theme;
};

/** Re-apply the last requested theme, still respecting a11y light override. */
export const reapplyThemeRespectingA11y = (): void => {
  applyAppTheme(lastRequestedTheme);
};

export const applyThemePreference = (preference: ThemePreference): AppTheme => {
  const resolved = resolveAppTheme(preference);
  applyAppTheme(resolved);
  return resolved;
};

export const getStoredThemePreference = (): AppTheme => {
  if (typeof window === 'undefined') return DEFAULT_APP_THEME;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    const normalized = normalizeThemePreference(stored);
    // Migrate legacy `system` (and any other non light/dark) to an explicit value.
    if (stored !== normalized) {
      window.localStorage.setItem(THEME_STORAGE_KEY, normalized);
    }
    return normalized;
  } catch {
    /* ignore */
  }
  return DEFAULT_APP_THEME;
};

export const persistThemePreference = (preference: ThemePreference): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      THEME_STORAGE_KEY,
      normalizeThemePreference(preference)
    );
  } catch {
    /* ignore */
  }
};

/** @deprecated OS follow is no longer used; kept for call-site compatibility. */
export const watchSystemTheme = (
  _onChange: (theme: AppTheme) => void
): (() => void) => {
  return () => undefined;
};

/** Apply the saved Light/Dark choice. Call on every client boot. */
export const bootstrapTheme = (): AppTheme => {
  const preference = getStoredThemePreference();
  return applyThemePreference(preference);
};
